/**
 * Reports: one description of what each section shows (docs/reports.md).
 *
 * sectionBlocks() turns a section's stored content into plain blocks —
 * key/value rows, tables, text, stat tiles, charts — and the page, the print
 * preview and the .docx all read those blocks, so the three never disagree
 * about a number.
 */

export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const NF = new Intl.NumberFormat('en-ZA');
const RF = new Intl.NumberFormat('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Space between thousands (en-ZA), but a decimal point, as the rest of the dashboard prints.
const dot = (s) => s.replace(',', '.');
export const fmtN = (n) => (n == null || n === '' ? '—' : dot(NF.format(Number(n))));
export const fmtR = (n) => (n == null || n === '' ? '—' : `R ${dot(RF.format(Number(n)))}`);
export const fmtPct = (n) => (n == null ? '—' : `${dot(Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 1 }))}%`);
export const monthLabel = (ym) => { const [y, m] = String(ym).split('-'); return `${MONTHS[Number(m) - 1] ?? m} ${y}`; };
export function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

export const KIND_LABEL = {
  profile: 'Typed details',
  data: 'Figures',
  narrative: 'Written by the agent',
  chart: 'Graphs',
};

/** Who fills a section in: the agent (prompt), the database (data/chart) or a person (profile). */
export const isAgentSection = (def) => !!def.prompt;

/** Plain paragraphs and "- " bullets → HTML. */
export function textHtml(text) {
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(`<ul>${list.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>`); list = []; } };
  for (const para of String(text || '').split(/\n\s*\n/)) {
    for (const line of para.split('\n')) {
      const t = line.trim();
      if (!t) continue;
      if (/^[-•*]\s+/.test(t)) list.push(t.replace(/^[-•*]\s+/, ''));
      else { flush(); out.push(`<p>${esc(t)}</p>`); }
    }
    flush();
  }
  return out.join('');
}

/** Paragraph strings for the .docx: bullets keep a "• ". */
export function textParagraphs(text) {
  return String(text || '').split('\n').map((l) => l.trim()).filter(Boolean)
    .map((l) => l.replace(/^[-•*]\s+/, '• '));
}

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : 'Not set');

/**
 * The blocks a section shows.
 * @returns {Array<{type:'kv',rows:[string,string][]}|{type:'table',head:string[],rows:string[][],align?:string[]}|{type:'text',text:string}|{type:'tiles',tiles:{label:string,value:string,note?:string}[]}|{type:'charts',block:string}|{type:'note',text:string}>}
 */
export function sectionBlocks(def, content) {
  if (!content) return [];
  const blocks = [];
  const d = content.data;

  if (def.kind === 'profile' && def.block === 'signatures') {
    blocks.push({ type: 'table', head: ['Role', 'Name and surname', 'Signature', 'Date'],
      rows: (content.rows || []).map((r) => [r.role, r.name || '', '', r.date ? fmtDate(r.date) : '']) });
  } else if (def.kind === 'profile') {
    blocks.push({ type: 'kv', rows: (def.fields || []).map((f) => [f.label, content.fields?.[f.key] || '']) });
  }

  if (def.kind === 'narrative' || (content.text && def.kind !== 'profile')) {
    if (content.text) blocks.push({ type: 'text', text: content.text });
  }

  if (d && def.block === 'beneficiaries') {
    blocks.push({ type: 'kv', rows: [
      ['Funding agreement year', d.agreement_year || 'Not set'],
      ['Total number of approved beneficiaries', d.approved == null ? 'Not set — add it on the Set-up tab' : fmtN(d.approved)],
      ['Beneficiaries enrolled', fmtN(d.enrolled)],
      ['Beneficiaries who left during implementation', fmtN(d.left)],
      ['Transferred out', fmtN(d.transferred)],
      ['Number of active beneficiaries (retained)', `${fmtN(d.retained)} (${fmtPct(d.retention_pct)} of enrolled)`],
      ['  of whom completed their programme', fmtN(d.completed)],
      ['  of whom still in training', fmtN(d.in_training)],
    ] });
  }

  if (d && def.block === 'interventions') {
    blocks.push({ type: 'table', head: ['Programme', 'NQF level', 'Credits', 'Beneficiaries', 'Retained', 'Completed'],
      align: ['l', 'c', 'r', 'r', 'r', 'r'],
      rows: (d.programmes || []).map((p) => [p.programme, p.nqf_level || '—', p.credits == null ? '—' : fmtN(p.credits), fmtN(p.beneficiaries), fmtN(p.retained), fmtN(p.completed)]) });
    const seta = (d.seta || []).filter((s) => s.body || s.accreditation);
    blocks.push({ type: 'kv', rows: [
      ...(seta.length ? seta.map((s) => [`SETA / QCTO: ${s.body || '—'}`, s.accreditation || '—']) : [['SETA / QCTO accreditation', 'Not set — add it on the Set-up tab']]),
      ['Training start date', fmtDate(d.start_date)],
      ['Training end date', fmtDate(d.end_date)],
      ['Beneficiaries registered with the relevant SETA / QCTO', yesNo(d.registered_with_seta)],
    ] });
  }

  if (d && def.block === 'placements') {
    if ((d.rows || []).length) {
      blocks.push({ type: 'table', head: ['Employer', 'Address', 'Contact', 'Sector', 'Absorbed', 'Programme'], align: ['l', 'l', 'l', 'l', 'r', 'l'],
        rows: d.rows.map((r) => [r.employer, r.address || '', r.contact || '', r.sector || '', fmtN(r.absorbed), r.programme || '']) });
    } else {
      blocks.push({ type: 'note', text: 'No employer placements recorded yet. Add them on the Set-up tab.' });
    }
  }

  if (d && def.block === 'finance') {
    blocks.push({ type: 'kv', rows: [
      ['Project value', d.project_value == null ? 'Not set — add it on the Set-up tab' : fmtR(d.project_value)],
      ['Paid by the paymaster to date', fmtR(d.total_paid)],
      ['Balance', d.project_value == null ? '—' : fmtR(Number(d.project_value) - Number(d.total_paid))],
    ] });
    if ((d.payments || []).length) {
      blocks.push({ type: 'table', head: ['Year', 'Month', 'Beneficiaries', 'Amount', 'Balance'], align: ['l', 'l', 'r', 'r', 'r'],
        rows: d.payments.map((p) => [String(p.year), MONTHS[p.month - 1], fmtN(p.beneficiaries), fmtR(p.amount), fmtR(p.balance)]) });
    } else {
      blocks.push({ type: 'note', text: 'No payments from the paymaster recorded yet. Add them on the Set-up tab.' });
    }
  }

  if (d && def.block === 'deliverables') {
    if (Array.isArray(d) && d.length) {
      blocks.push({ type: 'table', head: ['Planned deliverable', 'Target', 'Actual', 'Variance', 'Reason for variance'], align: ['l', 'r', 'r', 'r', 'l'],
        rows: d.map((m) => [
          m.deliverable + (m.due_date ? ` (by ${fmtDate(m.due_date)})` : ''),
          m.target == null ? '—' : fmtN(m.target),
          m.actual == null ? '—' : (isNaN(Number(m.actual)) ? m.actual : fmtN(m.actual)),
          m.variance == null ? '—' : `${m.variance > 0 ? '+' : ''}${fmtN(m.variance)}`,
          content.reasons?.[m.id] ?? m.reason ?? '',
        ]) });
    } else {
      blocks.push({ type: 'note', text: 'No milestones recorded yet. Add the deliverables from the implementation plan on the Set-up tab.' });
    }
  }

  if (d && def.block === 'demographics') {
    const g = Object.fromEntries((d.gender || []).map((x) => [x.label, x.n]));
    const src = d.gender_source || {};
    blocks.push({ type: 'tiles', tiles: [
      { label: 'Beneficiaries', value: fmtN(d.total) },
      { label: 'Women', value: fmtPct(d.total ? (100 * (g.Female || 0)) / d.total : null), note: `${fmtN(g.Female || 0)} learners` },
      { label: 'Youth (18–35)', value: fmtPct(d.youth?.youth_pct), note: `${fmtN(d.youth?.youth)} of those with a known age` },
      { label: 'With a disability', value: fmtN(d.disability?.yes) },
    ] });
    blocks.push({ type: 'charts', block: 'demographics' });
    blocks.push({ type: 'note', text: `Age is worked out from the SA ID number as at the report date (${fmtN(d.youth?.unknown)} without a valid ID or birth date). `
      + `Gender comes from the learner record, or the ID number where the record is blank (${fmtN(src.id_number || 0)} learners); ${fmtN(src.unknown || 0)} unknown.` });
  }

  if (d && def.block === 'progress') {
    blocks.push({ type: 'tiles', tiles: [
      { label: 'Classes', value: fmtN(d.classes), note: `${fmtN(d.venues)} venues · ${fmtN(d.municipalities)} municipalities` },
      { label: 'Attendance', value: fmtPct(d.attendance?.rate_pct), note: 'present ÷ marked present or absent' },
      { label: 'Dropout flags raised', value: fmtN(d.dropout_flags?.raised), note: `${fmtN(d.dropout_flags?.open)} still open` },
      { label: 'Stipends calculated', value: fmtR(d.stipends_total) },
    ] });
    blocks.push({ type: 'charts', block: 'progress' });
    blocks.push({ type: 'table', head: ['Month', 'Started', 'Left', 'In training', 'Attendance', 'Stipends (learners)', 'Stipends (R)'],
      align: ['l', 'r', 'r', 'r', 'r', 'r', 'r'],
      rows: (d.months || []).map((m) => [monthLabel(m.month), fmtN(m.starters), fmtN(m.left), fmtN(m.in_training), fmtPct(m.attendance_pct), fmtN(m.stipend_learners), fmtR(m.stipend_gross)]) });
  }

  return blocks;
}

/** Blocks → HTML. Charts become a placeholder the caller fills (canvases on screen, images in print). */
export function blocksHtml(blocks, chartSlot = () => '') {
  return blocks.map((b) => {
    if (b.type === 'text') return `<div class="rp-text">${textHtml(b.text)}</div>`;
    if (b.type === 'note') return `<p class="rp-note">${esc(b.text)}</p>`;
    if (b.type === 'kv') {
      return `<table class="rp-kv">${b.rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v).replace(/\n/g, '<br>') || '<span class="rp-blank">—</span>'}</td></tr>`).join('')}</table>`;
    }
    if (b.type === 'table') {
      const al = (i) => ({ r: 'right', c: 'center' }[b.align?.[i]] || 'left');
      return `<div class="table-wrap"><table class="rp-table"><thead><tr>${b.head.map((h, i) => `<th style="text-align:${al(i)}">${esc(h)}</th>`).join('')}</tr></thead>
        <tbody>${b.rows.map((r) => `<tr>${r.map((c, i) => `<td style="text-align:${al(i)}">${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
    }
    if (b.type === 'tiles') {
      // Report figures, not record counts: they describe the snapshot below and open nothing.
      return `<div class="rp-tiles">${b.tiles.map((t) => `<div class="rp-tile"><span>${esc(t.label)}</span><b>${esc(t.value)}</b>${t.note ? `<small>${esc(t.note)}</small>` : ''}</div>`).join('')}</div>`;
    }
    if (b.type === 'charts') return chartSlot(b.block);
    return '';
  }).join('');
}
